import React, { useState, useEffect, useRef } from 'react';
import { userHasRole } from '../../utils/roleHelpers';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Plus, Search, Filter, Send, Clock, Edit2, Trash2,
  RefreshCw, CheckCircle, AlertCircle, MessageSquare, 
  Users, X, Loader2, Eye, Calendar, Zap, ChevronRight,
  ChevronLeft, FileText, Play, Pause, Building2, Tag,
  Bold, Italic, Link, Smile, Paperclip, Image, File, Video
} from 'lucide-react';
import { whatsappAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import RecipientSelectionModal from './RecipientSelectionModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    draft: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Draft' },
    scheduled: { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Scheduled' },
    sending: { bg: 'bg-yellow-100', text: 'text-yellow-600', label: 'Sending' },
    sent: { bg: 'bg-green-100', text: 'text-green-600', label: 'Sent' },
    failed: { bg: 'bg-red-100', text: 'text-red-600', label: 'Failed' }
  };
  const c = config[status] || config.draft;
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// Broadcast Card Component
const BroadcastCard = ({ broadcast, onView, onEdit, onDelete, onSend, canDelete, sendingId }) => {
  const isSending = sendingId === broadcast.id;
  
  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all"
      data-testid={`broadcast-card-${broadcast.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">{broadcast.name}</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={broadcast.status} />
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Users size={12} />
              {broadcast.recipientCount || 0} recipients
            </span>
          </div>
        </div>
        <MessageSquare size={20} className="text-green-600" />
      </div>
      
      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
        {broadcast.message}
      </p>
      
      {/* Stats for sent broadcasts */}
      {broadcast.status === 'sent' && (
        <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-center">
            <p className="text-lg font-bold text-gray-800">{broadcast.sentCount || 0}</p>
            <p className="text-xs text-gray-500">Sent</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-green-600">{broadcast.deliveredCount || 0}</p>
            <p className="text-xs text-gray-500">Delivered</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-red-600">{broadcast.failedCount || 0}</p>
            <p className="text-xs text-gray-500">Failed</p>
          </div>
        </div>
      )}
      
      {/* Scheduled time */}
      {broadcast.status === 'scheduled' && broadcast.scheduledAt && (
        <div className="flex items-center gap-2 mb-4 p-2 bg-blue-50 rounded-lg text-sm text-blue-700">
          <Clock size={14} />
          Scheduled: {new Date(broadcast.scheduledAt).toLocaleString('en-IN')}
        </div>
      )}
      
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-500">
          {broadcast.createdAt && new Date(broadcast.createdAt).toLocaleDateString('en-IN')}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onView(broadcast)}
            className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
            title="View"
          >
            <Eye size={16} />
          </button>
          {broadcast.status === 'draft' && (
            <>
              <button
                onClick={() => onEdit(broadcast)}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={() => onSend(broadcast.id)}
                disabled={isSending}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                title="Send Now"
              >
                {isSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send
              </button>
            </>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(broadcast)}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteModal = ({ isOpen, onClose, onConfirm, name }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="text-red-600" size={20} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Delete Broadcast</h3>
        </div>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete <span className="font-medium">"{name}"</span>?
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// 4-Step Broadcast Wizard Modal
const BroadcastWizardModal = ({ isOpen, onClose, onSave, broadcast, templates }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    recipients: [],
    templateId: null,
    message: '',
    scheduledAt: '',
    delayBetweenMessages: 2,
    attachments: []
  });
  const [saving, setSaving] = useState(false);
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  
  useEffect(() => {
    if (broadcast) {
      setFormData({
        name: broadcast.name || '',
        recipients: broadcast.recipients || [],
        templateId: broadcast.templateId || null,
        message: broadcast.message || '',
        scheduledAt: broadcast.scheduledAt || '',
        delayBetweenMessages: broadcast.delayBetweenMessages || 2,
        attachments: broadcast.attachments || []
      });
    } else {
      setFormData({
        name: '',
        recipients: [],
        templateId: null,
        message: '',
        scheduledAt: '',
        delayBetweenMessages: 2,
        attachments: []
      });
    }
    setStep(1);
  }, [broadcast, isOpen]);
  
  // File upload handlers
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limit to 5 attachments per broadcast
    if (formData.attachments.length + files.length > 5) {
      alert('Maximum 5 attachments allowed per broadcast');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploadPromises = files.map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('folder', 'broadcasts');
        
        const response = await fetch(`${API_URL}/api/upload/file?folder=broadcasts`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataUpload
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Upload failed');
        }
        
        return response.json();
      });
      
      const results = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        attachments: [
          ...prev.attachments,
          ...results.map(r => ({
            fileId: r.file_id,
            filename: r.filename,
            url: r.url,
            contentType: r.content_type,
            size: r.size
          }))
        ]
      }));
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };
  
  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <Image size={16} className="text-blue-500" />;
    if (contentType?.startsWith('video/')) return <Video size={16} className="text-purple-500" />;
    return <File size={16} className="text-gray-500" />;
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  const handleTemplateSelect = (template) => {
    setFormData(prev => ({
      ...prev,
      templateId: template.id,
      message: template.message
    }));
  };
  
  const insertVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      message: prev.message + `{${variable}}`
    }));
  };
  
  const handleSubmit = async (sendNow = false, schedule = false) => {
    if (!formData.name || !formData.message || formData.recipients.length === 0) {
      alert('Please complete all required fields');
      return;
    }
    
    setSaving(true);
    try {
      // Convert the user's local datetime-local value to an ABSOLUTE UTC ISO
      // string before submit. Without this, the backend receives a naive
      // 'YYYY-MM-DDTHH:MM' and cannot unambiguously know what timezone the user
      // intended — which stranded past campaigns in "Overdue" status.
      let scheduledAtIso = null;
      if (schedule && formData.scheduledAt) {
        const local = new Date(formData.scheduledAt);
        if (!isNaN(local.getTime())) {
          scheduledAtIso = local.toISOString();
        } else {
          scheduledAtIso = formData.scheduledAt; // let backend fallback parse
        }
      }

      const payload = {
        name: formData.name,
        message: formData.message,
        recipients: formData.recipients,
        templateId: formData.templateId,
        scheduledAt: scheduledAtIso,
        delayBetweenMessages: formData.delayBetweenMessages,
        attachments: formData.attachments
      };
      
      await onSave(payload, sendNow);
      onClose();
    } catch (error) {
      console.error('Error saving broadcast:', error);
      alert('Failed to save broadcast');
    } finally {
      setSaving(false);
    }
  };
  
  const getPreviewMessage = () => {
    if (!formData.message) return '';
    const sampleData = {
      hotelName: 'Grand Hotel',
      ownerName: 'John Doe',
      phone: '9876543210',
      city: 'Mumbai',
      email: 'john@hotel.com',
      propertyName: 'Grand Hotel'
    };
    let preview = formData.message;
    Object.keys(sampleData).forEach(key => {
      preview = preview.replace(new RegExp(`\\{${key}\\}`, 'g'), sampleData[key]);
      preview = preview.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), sampleData[key]);
    });
    return preview;
  };
  
  const canProceed = () => {
    switch (step) {
      case 1: return formData.recipients.length > 0;
      case 2: return formData.message.length > 0;
      case 3: return true;
      case 4: return formData.name.length > 0;
      default: return false;
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl">
          {/* Header with Steps */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {broadcast ? 'Edit Broadcast' : 'Create New Broadcast'}
              </h3>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            {/* Step Indicators */}
            <div className="flex items-center justify-between">
              {['Select Recipients', 'Draft Message', 'Preview Variables', 'Send/Schedule'].map((label, idx) => (
                <div key={idx} className="flex items-center">
                  <div className={`flex items-center gap-2 ${idx < 3 ? 'flex-1' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step > idx + 1 ? 'bg-green-600 text-white' :
                      step === idx + 1 ? 'bg-teal-600 text-white' :
                      'bg-gray-200 text-gray-500'
                    }`}>
                      {step > idx + 1 ? <CheckCircle size={16} /> : idx + 1}
                    </div>
                    <span className={`text-sm hidden md:block ${step === idx + 1 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                      {label}
                    </span>
                  </div>
                  {idx < 3 && (
                    <div className={`h-0.5 w-12 mx-2 ${step > idx + 1 ? 'bg-green-600' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Step Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Step 1: Select Recipients */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="text-center py-8">
                  <Users size={48} className="mx-auto mb-4 text-gray-300" />
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Select Recipients</h4>
                  <p className="text-gray-500 mb-6">
                    Choose properties and leads to receive your broadcast message
                  </p>
                  <button
                    onClick={() => setShowRecipientModal(true)}
                    className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                    data-testid="select-recipients-btn"
                  >
                    {formData.recipients.length > 0 
                      ? `Edit Selection (${formData.recipients.length} selected)` 
                      : 'Select Recipients'}
                  </button>
                </div>
                
                {formData.recipients.length > 0 && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-medium text-gray-700">Selected Recipients</h5>
                      <span className="text-sm text-teal-600 font-medium">{formData.recipients.length} selected</span>
                    </div>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {formData.recipients.slice(0, 10).map(r => (
                        <span key={`${r.type}-${r.id}`} className="px-2 py-1 bg-white border border-gray-200 rounded text-sm flex items-center gap-1">
                          {r.type === 'property' ? <Building2 size={12} className="text-blue-600" /> : <Users size={12} className="text-purple-600" />}
                          {r.name}
                        </span>
                      ))}
                      {formData.recipients.length > 10 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-sm">
                          +{formData.recipients.length - 10} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Step 2: Draft Message */}
            {step === 2 && (
              <div className="space-y-6">
                {/* Template Selection */}
                {templates.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Use a Template (Optional)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {templates.map(t => (
                        <button
                          key={t.id}
                          onClick={() => handleTemplateSelect(t)}
                          className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                            formData.templateId === t.id
                              ? 'border-teal-500 bg-teal-50 text-teal-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <FileText size={14} className="inline mr-1" />
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Message Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    {/* Formatting Toolbar */}
                    <div className="flex items-center gap-1 p-2 bg-gray-50 border-b border-gray-200">
                      <button type="button" className="p-1.5 hover:bg-gray-200 rounded" title="Bold">
                        <Bold size={16} className="text-gray-600" />
                      </button>
                      <button type="button" className="p-1.5 hover:bg-gray-200 rounded" title="Italic">
                        <Italic size={16} className="text-gray-600" />
                      </button>
                      <button type="button" className="p-1.5 hover:bg-gray-200 rounded" title="Link">
                        <Link size={16} className="text-gray-600" />
                      </button>
                      <button type="button" className="p-1.5 hover:bg-gray-200 rounded" title="Emoji">
                        <Smile size={16} className="text-gray-600" />
                      </button>
                      <div className="h-4 w-px bg-gray-300 mx-2" />
                      <span className="text-xs text-gray-500">Variables:</span>
                      {['hotelName', 'ownerName', 'phone', 'city'].map(v => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => insertVariable(v)}
                          className="px-2 py-1 text-xs bg-teal-50 text-teal-700 rounded hover:bg-teal-100 transition-colors"
                        >
                          {`{${v}}`}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Type your message here..."
                      rows={8}
                      className="w-full px-4 py-3 outline-none resize-none"
                      data-testid="message-editor"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{formData.message.length} characters</p>
                </div>
                
                {/* File Attachments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attachments (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Attach images, videos, or documents. Max 5 files, 100MB each.
                  </p>
                  
                  {/* Upload Button */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.zip"
                    className="hidden"
                    data-testid="file-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || formData.attachments.length >= 5}
                    className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="upload-attachment-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 size={16} className="animate-spin text-teal-600" />
                        <span className="text-sm text-gray-600">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Paperclip size={16} className="text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {formData.attachments.length >= 5 ? 'Max files reached' : 'Add Attachment'}
                        </span>
                      </>
                    )}
                  </button>
                  
                  {/* Attached Files List */}
                  {formData.attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {formData.attachments.map((attachment, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
                          data-testid={`attachment-${idx}`}
                        >
                          <div className="flex items-center gap-3">
                            {getFileIcon(attachment.contentType)}
                            <div>
                              <p className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                                {attachment.filename}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(attachment.size)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(idx)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                            data-testid={`remove-attachment-${idx}`}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Step 3: Preview Variables */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Preview Message</h4>
                  <p className="text-sm text-gray-500 mb-4">
                    This is how your message will appear with variables filled in
                  </p>
                </div>
                
                {/* WhatsApp-style Preview */}
                <div className="max-w-md mx-auto">
                  <div className="bg-[#efeae2] rounded-xl p-4" style={{
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
                  }}>
                    <div className="bg-[#d9fdd3] rounded-lg p-3 shadow-sm max-w-[80%] ml-auto">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                        {getPreviewMessage() || 'Your message will appear here...'}
                      </p>
                      <div className="text-[10px] text-right text-gray-500 mt-1">
                        {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        <CheckCircle size={12} className="inline ml-1 text-blue-500" />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Variable Mapping Info */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-700 mb-3">Variable Mapping</h5>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <code className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">{'{hotelName}'}</code>
                      <span className="text-gray-600">Property/Hotel Name</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">{'{ownerName}'}</code>
                      <span className="text-gray-600">Owner Name</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">{'{phone}'}</code>
                      <span className="text-gray-600">Phone Number</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">{'{city}'}</code>
                      <span className="text-gray-600">City</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 4: Send/Schedule */}
            {step === 4 && (
              <div className="space-y-6">
                {/* Broadcast Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Broadcast Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., February Promotion"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                    data-testid="broadcast-name-input"
                  />
                </div>
                
                {/* Summary */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-700 mb-3">Broadcast Summary</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Recipients:</span>
                      <span className="font-medium">{formData.recipients.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Message Length:</span>
                      <span className="font-medium">{formData.message.length} characters</span>
                    </div>
                    {formData.templateId && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Template:</span>
                        <span className="font-medium">{templates.find(t => t.id === formData.templateId)?.name}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Delay Setting */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delay Between Messages (seconds)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={formData.delayBetweenMessages}
                    onChange={(e) => setFormData(prev => ({ ...prev, delayBetweenMessages: parseInt(e.target.value) || 2 }))}
                    className="w-32 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  />
                  <p className="mt-1 text-xs text-gray-500">Recommended: 2-5 seconds to avoid rate limits</p>
                </div>
                
                {/* Schedule Option */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule For (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.scheduledAt}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduledAt: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-white">
            <button
              onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={18} />
              Back
            </button>
            
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              
              {step < 4 ? (
                <button
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canProceed()}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight size={18} />
                </button>
              ) : (
                <>
                  <button
                    onClick={() => handleSubmit(false, false)}
                    disabled={saving || !canProceed()}
                    className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-medium disabled:opacity-50"
                  >
                    {saving ? <Loader2 size={18} className="animate-spin" /> : 'Save Draft'}
                  </button>
                  {formData.scheduledAt && (
                    <button
                      onClick={() => handleSubmit(false, true)}
                      disabled={saving || !canProceed()}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                    >
                      {saving ? <Loader2 size={18} className="animate-spin" /> : <Calendar size={18} />}
                      Schedule
                    </button>
                  )}
                  <button
                    onClick={() => handleSubmit(true, false)}
                    disabled={saving || !canProceed()}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                    data-testid="send-broadcast-btn"
                  >
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    Send Now
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Recipient Selection Modal */}
      <RecipientSelectionModal
        isOpen={showRecipientModal}
        onClose={() => setShowRecipientModal(false)}
        onSelect={(recipients) => setFormData(prev => ({ ...prev, recipients }))}
        initialSelected={formData.recipients}
      />
    </>
  );
};

/**
 * WhatsApp Broadcast Page - Main Component
 * Replaces the old BroadcastAutomation page with WhatsApp-only messaging
 */
const WhatsAppBroadcast = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [broadcasts, setBroadcasts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showWizard, setShowWizard] = useState(false);
  const [editBroadcast, setEditBroadcast] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, broadcast: null });
  const [sendingId, setSendingId] = useState(null);
  
  const isAdmin = userHasRole(user, 'admin');
  
  useEffect(() => {
    loadData();
    // Open wizard if navigating to /automation/new
    if (searchParams.get('new') === 'true') {
      setShowWizard(true);
    }
  }, []);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const [broadcastsRes, templatesRes] = await Promise.all([
        whatsappAPI.getBroadcasts(),
        whatsappAPI.getTemplates()
      ]);
      setBroadcasts(broadcastsRes.data.data || []);
      setTemplates(templatesRes.data.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async (formData, sendNow = false) => {
    if (editBroadcast) {
      await whatsappAPI.updateBroadcast(editBroadcast.id, formData);
      if (sendNow) {
        await whatsappAPI.sendBroadcast(editBroadcast.id);
      }
    } else {
      const response = await whatsappAPI.createBroadcast(formData);
      if (sendNow) {
        await whatsappAPI.sendBroadcast(response.data.id);
      }
    }
    loadData();
    setEditBroadcast(null);
  };
  
  const handleSend = async (broadcastId) => {
    try {
      setSendingId(broadcastId);
      await whatsappAPI.sendBroadcast(broadcastId);
      loadData();
    } catch (error) {
      console.error('Error sending broadcast:', error);
      alert('Failed to send broadcast');
    } finally {
      setSendingId(null);
    }
  };
  
  const handleDelete = async () => {
    if (!deleteModal.broadcast) return;
    
    try {
      await whatsappAPI.deleteBroadcast(deleteModal.broadcast.id);
      loadData();
      setDeleteModal({ isOpen: false, broadcast: null });
    } catch (error) {
      console.error('Error deleting broadcast:', error);
      if (error.response?.status === 403) {
        alert('Only Admin can delete broadcasts');
      }
    }
  };
  
  const filteredBroadcasts = broadcasts.filter(b => {
    const matchesSearch = !searchQuery || 
      b.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  
  const stats = {
    total: broadcasts.length,
    draft: broadcasts.filter(b => b.status === 'draft').length,
    scheduled: broadcasts.filter(b => b.status === 'scheduled').length,
    sent: broadcasts.filter(b => b.status === 'sent').length
  };
  
  return (
    <div className="space-y-6" data-testid="whatsapp-broadcast-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-200">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <MessageSquare className="text-green-600" size={24} />
            WhatsApp Broadcast
          </h2>
          <p className="text-sm text-gray-500 mt-1">Send bulk WhatsApp messages to properties and leads</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => { setEditBroadcast(null); setShowWizard(true); }}
            className="flex items-center px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm font-medium"
            data-testid="create-broadcast-btn"
          >
            <Plus size={20} className="mr-2" /> New Broadcast
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div 
          onClick={() => setStatusFilter('all')}
          className={`bg-white rounded-xl p-5 border-2 cursor-pointer transition-all hover:shadow-md ${
            statusFilter === 'all' ? 'border-teal-500 shadow-md' : 'border-transparent'
          }`}
        >
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div 
          onClick={() => setStatusFilter('draft')}
          className={`bg-white rounded-xl p-5 border-2 cursor-pointer transition-all hover:shadow-md ${
            statusFilter === 'draft' ? 'border-gray-500 shadow-md' : 'border-transparent'
          }`}
        >
          <p className="text-sm text-gray-500">Drafts</p>
          <p className="text-2xl font-bold text-gray-900">{stats.draft}</p>
        </div>
        <div 
          onClick={() => setStatusFilter('scheduled')}
          className={`bg-white rounded-xl p-5 border-2 cursor-pointer transition-all hover:shadow-md ${
            statusFilter === 'scheduled' ? 'border-blue-500 shadow-md' : 'border-transparent'
          }`}
        >
          <p className="text-sm text-gray-500">Scheduled</p>
          <p className="text-2xl font-bold text-blue-600">{stats.scheduled}</p>
        </div>
        <div 
          onClick={() => setStatusFilter('sent')}
          className={`bg-white rounded-xl p-5 border-2 cursor-pointer transition-all hover:shadow-md ${
            statusFilter === 'sent' ? 'border-green-500 shadow-md' : 'border-transparent'
          }`}
        >
          <p className="text-sm text-gray-500">Sent</p>
          <p className="text-2xl font-bold text-green-600">{stats.sent}</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search broadcasts..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            />
          </div>
          <button
            onClick={loadData}
            className="flex items-center px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Broadcasts Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      ) : filteredBroadcasts.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredBroadcasts.map(broadcast => (
            <BroadcastCard
              key={broadcast.id}
              broadcast={broadcast}
              onView={(b) => { setEditBroadcast(b); setShowWizard(true); }}
              onEdit={(b) => { setEditBroadcast(b); setShowWizard(true); }}
              onDelete={(b) => setDeleteModal({ isOpen: true, broadcast: b })}
              onSend={handleSend}
              canDelete={isAdmin}
              sendingId={sendingId}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No broadcasts found</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Create your first WhatsApp broadcast to get started'
            }
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <button
              onClick={() => setShowWizard(true)}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
            >
              Create Your First Broadcast
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      <BroadcastWizardModal
        isOpen={showWizard}
        onClose={() => { setShowWizard(false); setEditBroadcast(null); }}
        onSave={handleSave}
        broadcast={editBroadcast}
        templates={templates}
      />
      
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, broadcast: null })}
        onConfirm={handleDelete}
        name={deleteModal.broadcast?.name}
      />
    </div>
  );
};

export default WhatsAppBroadcast;
